/**
 * <Chart> — library-agnostic dispatcher.
 *
 * Reads the active chart library from `useChartLib()` (or accepts an
 * `libOverride` prop for side-by-side comparison views), then dispatches to
 * the matching implementation. Each library implementation (`recharts/`,
 * `visx/`, `d3/`, `echarts/`) renders the same data + theme; switching libs
 * should produce a visually equivalent chart with the same palette.
 *
 * Empty/missing data renders a centered empty-state message.
 *
 * Week 1: dispatcher + types + theme adapter only. Implementations land
 * Week 2+ — calling any chart today throws a clear "not yet wired" error.
 * See `.ai/plans/2026-05-19-0854-kchristo-neeve-feature-flagging-for-mvp.md`.
 */

import { useMemo } from 'react';
import { useChartLib } from './useChartLib';
import { renderRechartsChart } from './recharts';
import { renderVisxChart } from './visx';
import { renderD3Chart } from './d3';
import { renderEchartsChart } from './echarts';
import type { FullChartProps, ChartLib } from './types';

const renderers: Record<ChartLib, (props: FullChartProps) => JSX.Element> = {
  recharts: renderRechartsChart,
  visx: renderVisxChart,
  d3: renderD3Chart,
  echarts: renderEchartsChart,
};

function hasData(props: FullChartProps): boolean {
  switch (props.type) {
    case 'bar':
    case 'pie':
    case 'donut':
      return Array.isArray(props.data) && props.data.length > 0;
    case 'stacked-bar':
    case 'grouped-bar':
    case 'line':
    case 'area':
      return Array.isArray(props.data) && props.data.length > 0 && props.seriesKeys.length > 0;
    case 'combo':
      return Array.isArray(props.data) && props.data.length > 0;
    case 'geo':
      return Array.isArray(props.data) && props.data.length > 0;
    case 'sparkline-table':
      return Array.isArray(props.data) && props.data.length > 0;
  }
}

export function Chart(props: FullChartProps): JSX.Element {
  const [activeLib] = useChartLib();
  const lib = props.libOverride ?? activeLib;

  const height = props.height ?? 280;
  const empty = !hasData(props);

  // Per-chart-type library routing exceptions:
  //
  //  - Geo: only @visx/geo is implemented (uses d3-geo + a world-atlas
  //    TopoJSON). Every other lib (Recharts / D3 / ECharts) routes to Visx
  //    for type='geo' — that's the canonical implementation. Widgets that
  //    use geo render a small "Visx-only" caption so users don't think the
  //    slicer is broken.
  //  - Line/Area/Stacked-bar/Grouped-bar/Sparkline-table (Week 2-3 transition):
  //    only the Recharts implementation exists today. Non-Recharts libs fall
  //    back to Recharts so dashboards remain functional during the slicer
  //    comparison period. Visx / D3 / ECharts impls land in Week 3.
  const isUnimplementedInLib =
    lib !== 'recharts' &&
    ['line', 'area', 'stacked-bar', 'grouped-bar', 'combo', 'sparkline-table'].includes(props.type);
  const effectiveLib: ChartLib =
    props.type === 'geo' ? 'visx' : isUnimplementedInLib ? 'recharts' : lib;

  const content = useMemo(() => {
    if (empty) {
      return (
        <div
          className="dls-flex dls-items-center dls-justify-center dls-text-sm dls-text-muted-foreground"
          style={{ height }}
        >
          {props.emptyMessage ?? 'No data'}
        </div>
      );
    }
    const render = renderers[effectiveLib];
    return render(props);
  }, [empty, height, effectiveLib, props]);

  return (
    <div
      role="img"
      aria-label={props.ariaLabel}
      data-testid={props['data-testid']}
      data-chart-lib={lib}
      data-chart-effective-lib={effectiveLib}
      data-chart-type={props.type}
      style={{ width: '100%', height }}
    >
      {content}
    </div>
  );
}

// Note: type and utility re-exports live in `./types`, `./useChartLib`, and
// `./theme` respectively. Importing them through this file would break Vite's
// react-refresh for Chart.tsx (only-export-components rule).
